import { OutcomeHist } from "./OutcomeHist";
import { PeerMap } from "./PeerMap";
import { factorText } from "./srText";
import { SIZE_LABEL, SIZE_ADJ, signed, shortDate, qLabel, useBoxWidth, signalWord, signalRank, signalClass, yearSpan } from "./srUtil";

const tenths = (p) => Math.round(p / 10);

const verdict = (o) =>
  o.hit_pct >= 58 && o.median > 2
    ? ["Leans positive", "green", "The odds and the typical result favour beating the sector, but plenty of similar stocks still lost ground."]
    : o.hit_pct <= 45 || o.median < -1
      ? ["Leans negative", "red", "Similar stocks trailed their sector more often than they beat it."]
      : ["No clear edge", "amber", "Similar stocks did about as well as their sector, with wide swings either way."];

const comparisonDetail = (d) => {
  const o = d.long.outcome, m = d.long.outcome_meta, v = d.long.v3, sp = m.s10_minus_s1, band = v.score_decile;
  return {
    title: "How we worked this out",
    body: (
      <>
        <p><span className={signalClass(band)}>{signalWord(band)} signal · {SIZE_LABEL[o.size_group]}</span></p>
        <p>We found {o.n} past cases ({yearSpan(m.quarters)}) where funds bought a {SIZE_ADJ[o.size_group]} stock whose signal was in the
          same group as {d.symbol}’s ({signalRank(band).toLowerCase()} that quarter). For each one we measured how it did over the next
          six months against similar-sized stocks in its sector.</p>
        <p><b>What happened:</b> {Math.round(o.hit_pct)}% beat their sector, and {Math.round(o.p_gt10)}% beat it by more than 10%. The typical
          result was {signed(o.median)}%; the average was {signed(o.mean)}%, {o.mean >= o.median ? "pulled up by a few big winners" : "pulled down by a few big losers"}.
          8 in 10 landed between {signed(o.p10)}% and {signed(o.p90)}%.</p>
        <p><b>Has the signal been reliable?</b> Stocks with the strongest signals beat those with the weakest by about {Math.round(sp.mean)}% on
          average, and did so in {sp.quarters_positive} of the {sp.n_quarters} quarters we tested. Every result here comes from stocks we
          hadn’t used when designing the signal.</p>
        <p><b>When the six months start:</b> on {shortDate(v.reveal_date, true)}, when the funds’ buying became public, not today.
          {v.alpha_captured != null && ` ${d.symbol} has moved ${signed(v.alpha_captured)}% against its sector since then; that move is part of the range, not extra on top of it.`}</p>
        <p>Past results don’t guarantee future ones. This is evidence, not advice.</p>
      </>
    ),
  };
};

const peerDetail = (p, onRead) => ({
  title: p.symbol,
  body: (
    <>
      <p><span className={signalClass(p.score_decile)}>{signalWord(p.score_decile)} signal · {signalRank(p.score_decile)} this quarter</span></p>
      <p>{p.n_funds === 1 ? "1 fund opened a new position" : `${p.n_funds} funds opened new positions`}. Since they bought, the price has moved {signed(p.bp_rel * 100, 0)}%
        against its sector, ahead of {Math.round(p.bp_pct * 100)}% of similar stocks.</p>
      <button className="srlink" onClick={() => onRead(p.symbol)} data-testid="sr-peer-open-read">Open {p.symbol} →</button>
    </>
  ),
});

const Implied = ({ d }) => {
  const o = d.long.outcome, band = d.long.v3.score_decile;
  if (!o) return null;
  const [word, cls, note] = verdict(o);
  return (
    <section className="card pad implied fadein" data-testid="sr-implied">
      <div className="row between"><span className="eyebrow">What this implies · next 6 months</span>
        <span className={signalClass(band)} data-testid="sr-signal-pill">{signalWord(band)} signal · {signalRank(band)} this quarter</span></div>
      <h3 className={cls} data-testid="sr-implied-verdict">{word}</h3>
      <p className="impliedlead" data-testid="sr-implied-lead">Stocks with a signal like {d.symbol}’s beat their sector {tenths(o.hit_pct)} times
        in 10 over the following six months, with a typical result of {signed(o.median)}%.</p>
      <p className="muted" data-testid="sr-implied-range">8 in 10 landed between {signed(o.p10)}% and {signed(o.p90)}% against their sector. {note}</p>
      <small className="muted">The signal looks at who bought, how many funds bought, and how the price has moved since. The evidence is below.</small>
    </section>
  );
};

const OutcomeCard = ({ d, onDetail }) => {
  const o = d.long.outcome, band = d.long.v3.score_decile;
  const [ref, w] = useBoxWidth(560);
  if (!o) return <div className="card pad"><span className="eyebrow">How similar stocks turned out</span><p className="muted">Not enough history to show a range yet.</p></div>;
  return (
    <div className="card outcomePreview" data-testid="sr-outcome-card">
      <div className="row between previewhead"><div><span className="eyebrow">How similar stocks turned out</span>
        <h3>Six months later, against their sector</h3></div>
        <span className={signalClass(band)} data-testid="sr-outcome-band">{signalWord(band)} signal · {SIZE_LABEL[o.size_group]}</span></div>
      <div className="comparisonlayout">
        <div className="distribution">
          <div className="outcomelegend"><span><i className="redkey"></i>Trailed the sector</span><span><i className="greenkey"></i>Beat the sector</span></div>
          <div ref={ref}><OutcomeHist big width={w} pct={o.hist_pct} testid="sr-outcome-hist" title={`${o.n} similar past cases`} /></div>
          <div className="row between"><small data-testid="sr-outcome-count">Based on {o.n} similar past cases, {yearSpan(d.long.outcome_meta.quarters)}</small>
            <button className="textlink" onClick={() => onDetail(comparisonDetail(d))} data-testid="sr-comparison-detail">How we worked this out →</button></div>
        </div>
        <div className="setupstats">
          <div><strong className={o.median >= 0 ? "green" : "red"} data-testid="sr-outcome-median">{signed(o.median)}%</strong><span>Typical result against the sector</span></div>
          <div><strong data-testid="sr-outcome-gt10">{Math.round(o.p_gt10)}%</strong><span>Beat the sector by more than 10%</span></div>
          <div><strong className="red" data-testid="sr-outcome-trailed">{Math.round(100 - o.hit_pct)}%</strong><span>Trailed the sector</span></div>
        </div>
      </div>
    </div>
  );
};

const Factors = ({ d }) => {
  const t = factorText(d);
  return (
    <details className="card evidenceAccordion" id="evidence" open data-testid="sr-evidence">
      <summary data-testid="sr-evidence-toggle"><div><span className="eyebrow">The evidence</span><h3>Why we read it this way</h3>
        <small>Six things we check, in plain language</small></div><span className="expandicon">＋</span></summary>
      <div className="factorgrid">
        {d.long.factors.map((f) => (
          <article className="factorcard" key={f.id} data-testid={`sr-factor-${f.id}`}>
            <div className="row between"><span className="pill">{f.type}</span></div>
            <h4>{f.q}</h4><p className="fact">{f.fact}</p><p>{t[f.id]?.[0]}</p>
            <details><summary>How we measure it</summary><p>{t[f.id]?.[1]}</p></details>
          </article>
        ))}
      </div>
    </details>
  );
};

export const LongTerm = ({ d, onDetail, onRead }) => (
  <section className="section fadein" id="setup" style={{ marginTop: 0 }}>
    <div className="sectionhead"><span className="eyebrow">Long-term outlook · next 6 months</span><h2>What the fund buying implies</h2>
      <p>How stocks like this have done before, how {d.symbol} compares with similar stocks, and the evidence behind the read.</p></div>
    <Implied d={d} />
    <div className="longvisuals">
      <OutcomeCard d={d} onDetail={onDetail} />
      <section className="card pad longpeer">
        <div className="row between"><div><span className="eyebrow">Compared with similar stocks</span><h3>Where does {d.symbol} sit?</h3></div>
          <span className="pill" data-testid="sr-peer-count">{d.long.peers.length} similar stocks</span></div>
        <p className="muted">Each dot is a {SIZE_ADJ[d.long.size_group] || ""} {d.sector} stock that funds bought in {qLabel(d.quarter)}. Higher means a
          stronger signal; further right means the price has risen more since the funds bought.</p>
        <PeerMap peers={d.long.peers} symbol={d.symbol} onSelect={(p) => onDetail(peerDetail(p, onRead))} />
        <small>Click any dot to see that stock.</small>
      </section>
    </div>
    <Factors d={d} />
  </section>
);
